// Safari (and any browser with "Block All Cookies"/partitioned storage enabled) throws
// `DOMException: SecurityError: The operation is insecure.` just from ACCESSING
// `window.sessionStorage` / `window.localStorage` — not only from writing to it.
// That crash was reported in Rollbar (JPA-7375) from `ScrenTracker.trackPage()`, which threw
// inside a render-time effect and unmounted the whole page.
//
// This adapter never throws: it uses the native storage when available, falls back to an
// in-memory store when the browser blocks it, and is a no-op during SSR (Node has no per-request
// isolation, so a shared in-memory fallback there would leak data between users/requests).
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key'> & { length: number };

const PROBE_KEY = 'jliveStorageProbe';

const createMemoryStorage = (): StorageLike => {
  const store = new Map<string, string>();

  return {
    getItem: (key) => (store.has(key) ? store.get(key) : null),
    setItem: (key, value) => {
      store.set(key, value);
    },
    removeItem: (key) => {
      store.delete(key);
    },
    key: (index) => Array.from(store.keys())[index] ?? null,
    get length() {
      return store.size;
    },
  };
};

const noopStorage: StorageLike = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
  key: () => null,
  length: 0,
};

const isStorageUsable = (storage: Storage): boolean => {
  try {
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
};

const resolveStorage = (getNativeStorage: () => Storage): StorageLike => {
  if (typeof window === 'undefined') return noopStorage;

  try {
    const native = getNativeStorage();
    if (isStorageUsable(native)) return native;
  } catch {
    // Accessing the storage object itself threw (e.g. Safari with "Block All Cookies").
  }

  return createMemoryStorage();
};

const makeLazyStorage = (getNativeStorage: () => Storage): { get: () => StorageLike; reset: () => void } => {
  let instance: StorageLike | null = null;

  return {
    get: () => {
      if (!instance) instance = resolveStorage(getNativeStorage);
      return instance;
    },
    reset: () => {
      instance = null;
    },
  };
};

const lazySessionStorage = makeLazyStorage(() => window.sessionStorage);
const lazyLocalStorage = makeLazyStorage(() => window.localStorage);

export const safeSessionStorage: StorageLike = {
  getItem: (key) => lazySessionStorage.get().getItem(key),
  setItem: (key, value) => lazySessionStorage.get().setItem(key, value),
  removeItem: (key) => lazySessionStorage.get().removeItem(key),
  key: (index) => lazySessionStorage.get().key(index),
  get length() {
    return lazySessionStorage.get().length;
  },
};

export const safeLocalStorage: StorageLike = {
  getItem: (key) => lazyLocalStorage.get().getItem(key),
  setItem: (key, value) => lazyLocalStorage.get().setItem(key, value),
  removeItem: (key) => lazyLocalStorage.get().removeItem(key),
  key: (index) => lazyLocalStorage.get().key(index),
  get length() {
    return lazyLocalStorage.get().length;
  },
};

// Test-only: force re-resolution of the cached storage backend between test cases.
export const resetSafeStorageForTests = (): void => {
  lazySessionStorage.reset();
  lazyLocalStorage.reset();
};
